import { useEffect, useRef } from "react";

import wideJpg from "../../assets/hero-bullion-2400.jpg";
import wideWebp from "../../assets/hero-bullion-2400.webp";
import midJpg from "../../assets/hero-bullion-1600.jpg";
import midWebp from "../../assets/hero-bullion-1600.webp";
import tallJpg from "../../assets/hero-bullion-tall-1100.jpg";
import tallWebp from "../../assets/hero-bullion-tall-1100.webp";

/**
 * The hero: one photograph, three layers of light over it, and a pointer field
 * driving all of them.
 *
 * ## Why a photograph
 *
 * The bars used to be drawn — five SVG ingots, three gradient faces each,
 * stacked back to front. It was a careful drawing and it never once looked
 * like metal. Gold is almost entirely specular: what identifies it is not its
 * colour but the way a rough cast surface breaks one light source into
 * hundreds of separate highlights, and the way it throws that light back onto
 * whatever stands beside it. Gradients do neither. They produce a smooth
 * object, and a smooth gold object reads as plastic.
 *
 * So the subject is photographic now, and every layer around it exists to keep
 * it looking photographed rather than pasted on: nothing is drawn over the
 * metal, and the photograph has no visible edge anywhere on screen.
 *
 * ## The layers
 *
 * Back to front — the plate, the light, the market, the scrim, the copy.
 *
 * The **plate** is the photograph, full-bleed and `object-fit: cover`, so the
 * hero is the frame rather than a picture sitting inside one.
 *
 * The **glow** is a warm radial in `screen`, so it adds light instead of
 * painting over. Carried by the pointer, it is what makes the metal change
 * value as the cursor crosses it — additive light on a real surface, which is
 * the one kind of interactivity a photograph accepts without beginning to look
 * synthetic.
 *
 * The **market** is the XAUUSD reference. It is kept, because the product is a
 * gold trading system, and it is removed from everywhere it would read as a
 * chart: masked into the dark middle-left haze at very low opacity, never
 * crossing the bullion, carrying no axis, no figures and no direction. A line
 * that only rises would be a performance claim.
 *
 * The **scrim** does the actual blending. Four gradients on one element: a
 * horizontal wash taking the left of the frame down to the page's own black,
 * so the headline sits on ink and not on a photograph; a bottom fade, because
 * the stone slab in this frame runs bright all the way to the edge and would
 * otherwise collide with the section beneath it; a short top fade that settles
 * the image under the navigation; and a vignette to close the stage.
 *
 * ## The pointer field
 *
 * One `pointermove` listener, one `requestAnimationFrame` loop, no React
 * state — a re-render per pointer event would be several hundred
 * reconciliations a second for a purely visual effect. The loop eases towards
 * the pointer rather than snapping to it (Kowalski's point about
 * mouse-tracking: a value tied directly to the cursor feels artificial because
 * it has no momentum of its own), and the same easing settles everything when
 * the pointer leaves — the target returns to zero and the loop glides there.
 * Nothing snaps back, and an interruption mid-glide simply retargets.
 *
 * The loop parks itself once the field is within a fraction of a pixel of rest
 * and nothing is engaged, so an idle page costs nothing.
 *
 * Each layer moves by a different amount and the plate moves *against* the
 * cursor, which is what reads as depth. The amplitudes are deliberately small:
 * the photograph is the realism anchor, and a plate that visibly slides is a
 * parallax demo rather than a hero.
 *
 * ## Transforms, and who owns which
 *
 * Three nested elements around the image, because three things animate it and
 * they must not overwrite one another: the entrance owns the outer and touches
 * only opacity and blur, the pointer loop writes inline styles on the middle,
 * and the ambient drift owns the image itself.
 *
 * The drift is `alternate infinite`, so its last frame *is* its first frame.
 * That is the whole reason this is a still and not a clip: generated video
 * cannot be made to return to its opening frame, so it cuts, and a hero that
 * cuts every few seconds is worse than one that never moves. This loops
 * exactly, forever, for no extra bytes.
 *
 * ## Degrading
 *
 * The listener is never attached without a fine pointer, so a touch screen
 * pays for none of it. Under `prefers-reduced-motion` it is not attached
 * either, the entrance, the drift and the trace are switched off in CSS, and
 * what remains is the finished composition, sharp and still, from the first
 * frame.
 */
function GoldHero({ children }) {
  const heroRef = useRef(null);
  const plateRef = useRef(null);
  const dataRef = useRef(null);
  const glowRef = useRef(null);
  const copyRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof window.matchMedia !== "function") return undefined;

    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!fine.matches || still.matches) return undefined;

    const target = { x: 0, y: 0 };
    const eased = { x: 0, y: 0 };
    let frame = 0;
    let engaged = false;

    /* Cached, because reading it inside the move handler forces a layout on
       every event and a gaming mouse fires a thousand of them a second. */
    let box = hero.getBoundingClientRect();
    const remeasure = () => { box = hero.getBoundingClientRect(); };

    function paint() {
      // Ease towards the pointer instead of tracking it: the lag is the part
      // that reads as weight, and it doubles as the settle when it returns.
      eased.x += (target.x - eased.x) * 0.075;
      eased.y += (target.y - eased.y) * 0.075;
      const { x, y } = eased;

      // The plate moves against the cursor, and least of all: it is a heavy
      // object a long way back, and it is the one thing that must never look
      // like it is sliding.
      if (plateRef.current) {
        plateRef.current.style.transform =
          `translate3d(${(x * -11).toFixed(2)}px, ${(y * -7).toFixed(2)}px, 0)`;
      }
      if (dataRef.current) {
        dataRef.current.style.transform =
          `translate3d(${(x * -22).toFixed(2)}px, ${(y * -13).toFixed(2)}px, 0)`;
      }
      // The light travels furthest, which is what sells it as a source with a
      // position rather than as a painted highlight.
      if (glowRef.current) {
        glowRef.current.style.transform =
          `translate3d(${(x * 46).toFixed(2)}px, ${(y * 30).toFixed(2)}px, 0)`;
      }
      // Barely anything, and with the cursor: just enough that the copy sits
      // on a nearer plane than the metal.
      if (copyRef.current) {
        copyRef.current.style.transform =
          `translate3d(${(x * 5).toFixed(2)}px, ${(y * 3).toFixed(2)}px, 0)`;
      }

      const atRest =
        Math.abs(target.x - eased.x) < 0.0012 && Math.abs(target.y - eased.y) < 0.0012;
      if (atRest && !engaged) {
        frame = 0;
        return;
      }
      frame = window.requestAnimationFrame(paint);
    }

    function wake() {
      if (!frame) frame = window.requestAnimationFrame(paint);
    }

    function onMove(event) {
      target.x = ((event.clientX - box.left) / box.width) * 2 - 1;
      target.y = ((event.clientY - box.top) / box.height) * 2 - 1;
      engaged = true;
      wake();
    }

    function onLeave() {
      // Not a reset: the target goes home and the same easing carries
      // everything back, so leaving looks like the reverse of arriving.
      target.x = 0;
      target.y = 0;
      engaged = false;
      wake();
    }

    hero.addEventListener("pointermove", onMove, { passive: true });
    hero.addEventListener("pointerleave", onLeave, { passive: true });
    window.addEventListener("resize", remeasure, { passive: true });
    window.addEventListener("scroll", remeasure, { passive: true });

    return () => {
      hero.removeEventListener("pointermove", onMove);
      hero.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="lp-hero" ref={heroRef}>
      {/* The stage is decorative in full. The headline carries the meaning, so
          a screen reader is told about none of it. */}
      <div className="lp-stage" aria-hidden="true">
        <div className="lp-plate">
          <div className="lp-plate__par" ref={plateRef}>
            <picture>
              {/* Phones get their own crop rather than a shrunk landscape.
                  Cropped into the stack the metal stays the size it ought to
                  be, and the file is smaller than the frame it replaces. */}
              <source media="(max-width: 700px)" type="image/webp" srcSet={tallWebp} />
              <source media="(max-width: 700px)" type="image/jpeg" srcSet={tallJpg} />
              <source
                type="image/webp"
                srcSet={`${midWebp} 1600w, ${wideWebp} 2400w`}
                sizes="100vw"
              />
              <img
                className="lp-plate__img"
                src={wideJpg}
                srcSet={`${midJpg} 1600w, ${wideJpg} 2400w`}
                sizes="100vw"
                alt=""
                width="2400"
                height="1340"
                /* The largest paint on the page; it should not be queued
                   behind requests the stylesheet happens to make first. */
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </div>
        </div>

        {/* Light, not a surface: `screen` adds to the metal underneath instead
            of covering it, which is why this can follow the pointer without
            the photograph starting to look like a composite. */}
        <div className="lp-glow" ref={glowRef} />

        {/* The market, as texture. Masked into the haze on the dark side of
            the frame, so it never crosses the gold. */}
        <div className="lp-market" ref={dataRef}>
          <svg viewBox="0 0 1200 420" preserveAspectRatio="xMidYMid slice" focusable="false">
            <path
              className="lp-market__trace"
              d="M-20 250 L60 232 L120 268 L180 214 L240 240 L300 186 L360 226 L420 158
                 L480 204 L540 170 L600 232 L660 196 L720 148 L780 210 L840 176 L900 238
                 L960 192 L1020 246 L1080 202 L1140 236 L1220 198"
            />
          </svg>
        </div>

        {/* Four gradients, last, so they sit over the light and the trace and
            the copy bed stays ink. */}
        <div className="lp-scrim" />
      </div>

      <div className="lp-hero__inner">
        <div className="lp-hero__copy" ref={copyRef}>{children}</div>
      </div>
    </section>
  );
}

export default GoldHero;
